import clipArtApi from "@/api/clip-art";
import storageApi from "@/api/storage";
import ToggleInput from "@/components/common/ToggleInput";
import { NO_IMAGE_SRC_PATH } from "@/constants/image";
import { ClipArt } from "@/interfaces/clip-art.interface";
import { uploadClipArtImage, uploadClipArtThumbnail } from "@/utils/clip-art";
import { compressImage } from "@/utils/func";
import {
  Loader,
  MoveUpRight,
  PencilLine,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import Image from "next/image";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { ChangeEvent, useEffect, useRef, useState } from "react";

interface Props {
  data: ClipArt;
}

const ClipArtOptionId = ({ data }: Props) => {
  const [optionId, setOptionId] = useState(0);

  useEffect(() => {
    setOptionId(data.optionId);
  }, [data]);

  const onChange = (value: string) => {
    const newOptionId = Number(value.trim());
    if (!newOptionId || newOptionId === optionId) return;
    setOptionId(newOptionId);
    clipArtApi
      .updateOneById(data._id, { optionId: newOptionId })
      .then(() => (data.optionId = newOptionId))
      .catch(() => setOptionId(data.optionId));
  };

  return (
    <div className="hover:shadow-border group relative flex cursor-pointer rounded">
      <ToggleInput
        inputClassName="max-h-8"
        type="number"
        defaultValue={String(optionId)}
        onAfterChange={onChange}
      />
    </div>
  );
};

const ClipArtImage = ({ data }: Props) => {
  const [imageSrc, setImageSrc] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImageSrc(storageApi.getURLFromPath(data.path));
  }, [data]);

  const onUpload = () => inputRef.current?.click();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    uploadClipArtImage(data, file)
      .then((path) => {
        data.path = path || "";
        setImageSrc(`${storageApi.getURLFromPath(path)}`);
      })
      .catch(() => setLoading(false))
      .finally(() => (event.target.value = ""));
  };

  return (
    <>
      <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
        <Image
          crossOrigin="anonymous"
          className="rounded object-contain"
          alt={data.name}
          src={imageSrc || NO_IMAGE_SRC_PATH}
          width={200}
          height={200}
          onError={() => setImageSrc(NO_IMAGE_SRC_PATH)}
          onLoad={() => setLoading(false)}
        />

        <div
          className="absolute hidden h-full w-full items-center justify-center gap-2 bg-[#696b7073] group-hover:flex"
          onClick={onUpload}
        >
          <PencilLine size={20} className="cursor-pointer text-white" />
          <MoveUpRight
            className={`${imageSrc ? "block" : "hidden"} cursor-pointer text-white`}
            size={20}
            onClick={(e) => {
              e.stopPropagation();
              if (imageSrc) {
                window.open(imageSrc, "_blank");
              }
            }}
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onChange}
          />
        </div>
        {loading && (
          <div
            className="absolute flex h-full w-full items-center justify-center bg-[#696b7073]"
            onClick={onUpload}
          >
            <Loader className="animate-spin text-white" />
          </div>
        )}
      </div>
    </>
  );
};

const ClipArtThumbnail = ({ data }: Props) => {
  const [imageSrc, setImageSrc] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImageSrc(storageApi.getURLFromPath(data.thumbnailPath));
  }, [data]);

  const onUpload = () => inputRef.current?.click();

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0];
    if (file) {
      file = (await compressImage(file)) || file;
    }
    if (!file) return;
    setLoading(true);
    uploadClipArtThumbnail(data, file)
      .then((path) => {
        data.thumbnailPath = path;
        setImageSrc(`${storageApi.getURLFromPath(path)}`);
      })
      .catch(() => setLoading(false))
      .finally(() => (event.target.value = ""));
  };

  return (
    <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
      <Image
        crossOrigin="anonymous"
        className="object-contain"
        alt={data.name}
        src={imageSrc || NO_IMAGE_SRC_PATH}
        width={100}
        height={100}
        onError={() => setImageSrc(NO_IMAGE_SRC_PATH)}
        onLoad={() => setLoading(false)}
      />
      <div
        className="absolute hidden h-full w-full items-center justify-center bg-gray-300 opacity-50 group-hover:flex hover:flex"
        onClick={onUpload}
      >
        <PencilLine />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onChange}
        />
      </div>
      {loading && (
        <div
          className="absolute flex h-full w-full items-center justify-center bg-gray-300 opacity-50"
          onClick={onUpload}
        >
          <Loader className="animate-spin" />
        </div>
      )}
    </div>
  );
};

const ClipArtPreview = ({ data }: Props) => {
  const [imageSrc, setImageSrc] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImageSrc(storageApi.getURLFromPath(data.previewPath));
  }, [data]);

  return (
    <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
      <Image
        crossOrigin="anonymous"
        className="object-contain"
        alt={data.name}
        src={imageSrc || NO_IMAGE_SRC_PATH}
        width={100}
        height={100}
        onError={() => setImageSrc(NO_IMAGE_SRC_PATH)}
        onLoad={() => setLoading(false)}
      />
      {loading && (
        <div className="absolute flex h-full w-full items-center justify-center bg-gray-300 opacity-50">
          <Loader className="animate-spin" />
        </div>
      )}
    </div>
  );
};

const ClipArtName = ({ data }: Props) => {
  const onChange = (value: string) => {
    const newName = value.trim();
    if (!newName || newName === data.name) return;
    data.name = newName;
    clipArtApi
      .updateOneById(data._id, { name: newName })
      .then(() => {})
      .catch(() => {});
  };

  return (
    <div className="hover:shadow-border cursor-pointer rounded">
      <ToggleInput
        inputClassName="max-h-8"
        defaultValue={String(data.name)}
        onAfterChange={onChange}
      />
    </div>
  );
};

const ClipArtTooltip = ({ data }: Props) => {
  const onChange = (value: string) => {
    const newTooltip = value.trim();
    if (newTooltip === (data.tooltip ?? "")) return;
    data.tooltip = newTooltip;
    clipArtApi
      .updateOneById(data._id, { tooltip: newTooltip })
      .then(() => {})
      .catch(() => {});
  };

  return (
    <div className="hover:shadow-border cursor-pointer rounded">
      <ToggleInput
        inputClassName="max-h-8"
        defaultValue={data.tooltip ?? ""}
        onAfterChange={onChange}
      />
    </div>
  );
};

const ClipArtActions = ({
  data,
  onDelete,
}: Props & {
  onDelete?: (clipArt: ClipArt) => void;
}) => {
  const onConfirmDelete = (event: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: "This action cannot be undone. Confirm?",
      icon: <TriangleAlert color="red" />,
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => onDelete?.(data),
      reject: () => {},
    });
  };

  return (
    <div className="flex gap-2">
      <Button
        severity="danger"
        outlined
        style={{
          width: 28,
          height: 28,
          backgroundColor: "white",
        }}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
        icon={<Trash2 size={18} />}
        onClick={onConfirmDelete}
      />
    </div>
  );
};
export {
  ClipArtActions,
  ClipArtImage,
  ClipArtName,
  ClipArtOptionId,
  ClipArtPreview,
  ClipArtThumbnail,
  ClipArtTooltip,
};
